<template>
  <div
    ref="root"
    class="w-full overflow-hidden"
    :style="{ 'aspect-ratio': cssAspectRatio }"
  >
    <!-- Parallax on/off branch -->
    <AnimationsGsapOnScroll
      v-if="!disableParallax"
      class="w-full"
    >
      <picture class="w-full h-full">
        <!-- one <source> per breakpoint -->
        <source
          v-for="entry in imageData"
          :key="entry.breakpoint"
          :media="entry.breakpoint === smallestBreakpoint ? undefined : `(min-width: ${entry.size}px)`"
          :srcset="entry.srcset"
          :sizes="sizes"
        />
        <!-- fallback <img> -->
        <img
          ref="image"
          class="object-cover w-full h-full"
          data-scroll
          loading="lazy"
          :alt="alt || 'Default alt tag'"
          :src="fallbackSrc"
        />
      </picture>
    </AnimationsGsapOnScroll>

    <!-- when parallax is disabled -->
    <picture
      v-else
      class="w-full h-full"
    >
      <source
        v-for="entry in imageData"
        :key="entry.breakpoint"
        :media="entry.breakpoint === smallestBreakpoint ? undefined : `(min-width: ${entry.size}px)`"
        :srcset="entry.srcset"
        :sizes="sizes"
      />
      <img
        ref="image"
        class="object-cover w-full h-full"
        data-scroll
        loading="lazy"
        :alt="alt || 'Default alt tag'"
        :src="fallbackSrc"
      />
    </picture>
  </div>
</template>

<script setup>
import imageUrlBuilder from '@sanity/image-url';
import resolveConfig from 'tailwindcss/resolveConfig';
import tailwindConfig from './../tailwind.config';

const props = defineProps({
  aspectRatios: {
    type: Array,
    default: () => [
      { sm: 'default' },
      { md: 'default' },
      { lg: 'default' },
      { xl: 'default' },
      { '2xl': 'default' },
      { '3xl': 'default' }
    ]
  },
  refImage: String,
  isLazyloaded: Boolean,
  disableParallax: Boolean,
  alt: String
});

// Widths offered to the browser in each srcset. A fixed set keeps the number of
// distinct image transformations small, so most requests are Sanity CDN cache hits.
const SRCSET_WIDTHS = [320, 480, 640, 800, 1024, 1280, 1600, 1920];

const root = ref(null);
const picture = reactive({ asset: null, width: 0, height: 0 });
const imageData = ref([]);
const fallbackSrc = ref('');
// rendered width of the image, lets the browser pick the smallest fitting srcset candidate
const renderedWidth = ref(0);

const sanity = useSanity();
const { loadMedia, getMedia } = useSanityMediaLoader();
// pull Tailwind breakpoints
const screens = resolveConfig(tailwindConfig).theme.screens;
// track viewport width and rendered width (a ResizeObserver also catches
// containers that change size without a window resize, e.g. sliders or accordions)
const windowWidth = ref(0);
let resizeObserver = null;
function updateWidth() {
  windowWidth.value = window.innerWidth;
  renderedWidth.value = root.value?.offsetWidth || 0;
}

const sizes = computed(() => (renderedWidth.value ? `${renderedWidth.value}px` : '100vw'));

// build and sort breakpoints descending
const breakpoints = computed(() =>
  Object.entries(screens)
    .map(([bp, px]) => ({
      bp,
      minWidth: parseInt(px.replace('px', ''), 10),
      ratio: props.aspectRatios.find((r) => Object.keys(r)[0] === bp)?.[bp] || 'default'
    }))
    .sort((a, b) => b.minWidth - a.minWidth)
);

// helper to build Sanity URLs
function createBuilder() {
  return imageUrlBuilder(sanity.config);
}

// calculate height for a given width + ratio
function getAspectRatioHeight(width, ratio) {
  if (ratio === 'square') return width;
  if (ratio === 'panorama') return Math.round((width / 147) * 36.75);
  if (ratio.includes(':')) {
    const [wR, hR] = ratio.split(':').map(parseFloat);
    return Math.round((width / wR) * hR);
  }
  // 'default' → preserve original aspect
  return Math.round((width * picture.height) / picture.width);
}

// candidate widths for the srcset, never upscaling the original image
function getSrcsetWidths() {
  const widths = SRCSET_WIDTHS.filter((w) => w < picture.width);
  if (picture.width <= SRCSET_WIDTHS[SRCSET_WIDTHS.length - 1]) {
    widths.push(picture.width);
  }
  return widths;
}

// build the array of { breakpoint, size, srcset, ratio }
function generateImageData() {
  const widths = getSrcsetWidths();
  return breakpoints.value.map(({ bp, minWidth, ratio }) => ({
    breakpoint: bp,
    size: minWidth,
    srcset: widths.map((w) => `${getImageUrl(w, getAspectRatioHeight(w, ratio))} ${w}w`).join(', '),
    ratio
  }));
}

// get the URL for a given w/h
function getImageUrl(w, h) {
  return createBuilder()
    .image(picture.asset)
    .width(w)
    .height(h)
    .auto('format')
    .dpr(1)
    .fit('crop')
    .url();
}

// pick the smallest breakpoint key (catch-all source without media query)
const smallestBreakpoint = computed(() => imageData.value[imageData.value.length - 1]?.breakpoint);

// pick which ratio applies now (by checking windowWidth)
const currentAspectRatio = computed(() => {
  for (const { minWidth, ratio } of breakpoints.value) {
    if (windowWidth.value >= minWidth) {
      return ratio;
    }
  }
  // fallback to the last
  return breakpoints.value[breakpoints.value.length - 1]?.ratio || 'default';
});

// convert “3:1”→“3/1”, “square”→“1/1”, or natural
const cssAspectRatio = computed(() => {
  const r = currentAspectRatio.value;
  if (r === 'default') {
    return picture.width && picture.height ? `${picture.width}/${picture.height}` : 'auto';
  }
  if (r === 'square') return '1/1';
  if (r === 'panorama') return '147/36.75';
  if (r.includes(':')) return r.replace(':', '/');
  return 'auto';
});

// ——— Fetch & setup ———
function applyAsset(asset) {
  if (!asset?.metadata?.dimensions) return;
  picture.asset = asset;
  picture.width = asset.metadata.dimensions.width;
  picture.height = asset.metadata.dimensions.height;

  // build each source’s dimensions
  imageData.value = generateImageData();

  // fallback for browsers without <picture> support
  const fallbackWidth = Math.min(picture.width, 1280);
  fallbackSrc.value = getImageUrl(fallbackWidth, getAspectRatioHeight(fallbackWidth, currentAspectRatio.value));
}

// Media documents are normally resolved up front (see primeMedia), so the image is part of the
// server rendered HTML and no request is needed in the browser.
const knownDoc = props.refImage ? getMedia(props.refImage) : null;
const isResolved = knownDoc !== undefined;
if (knownDoc) {
  applyAsset(knownDoc.asset);
}

onMounted(async () => {
  // watch for resize
  updateWidth();
  window.addEventListener('resize', updateWidth);
  resizeObserver = new ResizeObserver(updateWidth);
  resizeObserver.observe(root.value);

  // fallback for images that were not part of the prefetched content,
  // loaded in one batched request together with all other pictures
  if (!isResolved) {
    const doc = await loadMedia(props.refImage);
    applyAsset(doc?.asset);
  }
});

onUnmounted(() => {
  window.removeEventListener('resize', updateWidth);
  resizeObserver?.disconnect();
});
</script>

<style lang="postcss">
img {
  @apply w-full;
}
</style>
